import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

interface AutocompleteProps<T> {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (option: T | null) => void;
  searchFunction: (query: string) => Promise<T[]>;
  debounceMs?: number;
  className?: string;
  style?: React.CSSProperties;
  displayKey: keyof T;
  subDisplayKey?: keyof T;
}

const Autocomplete = <T extends Record<string, any>>({
  placeholder = 'Search...',
  value,
  onChange,
  onSelect,
  searchFunction,
  debounceMs = 300,
  className = '',
  style = {},
  displayKey,
  subDisplayKey,
}: AutocompleteProps<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (selectedOption && value === selectedOption[displayKey]) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    if (value.length < 2) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchFunction(value);
        setOptions(results);
        setIsOpen(results.length > 0);
      } catch (error) {
        console.error('Search error:', error);
        setOptions([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, searchFunction, debounceMs, selectedOption, displayKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (!newValue) {
      setSelectedOption(null);
      onSelect(null);
    } else if (selectedOption && newValue !== selectedOption[displayKey]) {
      setSelectedOption(null);
      onSelect(null);
    }
  };

  const handleOptionClick = (option: T) => {
    setSelectedOption(option);
    onChange(option[displayKey]);
    onSelect(option);
    setIsOpen(false);
    setOptions([]);
  };

  const handleClear = () => {
    setSelectedOption(null);
    onChange('');
    onSelect(null);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (options.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay closing to allow option clicks
    setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div className="relative" style={{ minWidth: '16rem', maxWidth: '16rem' }}>
      <div className="relative">
        <input
          type="text"
          value={selectedOption ? selectedOption[displayKey] : value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="w-full h-12 px-10 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-base"
          style={{ minWidth: '16rem', maxWidth: '16rem' }}
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        {selectedOption && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {!selectedOption && (
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto" style={{ minWidth: '16rem', maxWidth: '16rem' }}>
          {isLoading ? (
            <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-center">
              Searching...
            </div>
          ) : options.length > 0 ? (
            options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option)}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none rounded-lg"
              >
                <div className="font-medium text-gray-900 dark:text-white">{option[displayKey]}</div>
                {subDisplayKey && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">{option[subDisplayKey]}</div>
                )}
              </button>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-center">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
